<template>
    <div
        ref="containerRef"
        class="app-loading-lottie"
        :style="containerStyle"
        role="img"
        :aria-label="ariaLabel"
    ></div>
</template>

<script setup lang="ts">
import type { AnimationItem } from 'lottie-web'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import loadingAnimation from '~/assets/loading-output.json'

const props = withDefaults(defineProps<{
    size?: number | string
    loop?: boolean
    autoplay?: boolean
    ariaLabel?: string
}>(), {
    size: 32,
    loop: true,
    autoplay: true,
    ariaLabel: 'Loading',
})

const containerRef = ref<HTMLElement | null>(null)
let animation: AnimationItem | null = null

const normalizeSize = (value: number | string) => {
    if (typeof value === 'number') return `${value}px`
    const trimmed = value.trim()
    return /^\d+$/.test(trimmed) ? `${trimmed}px` : trimmed
}

const containerStyle = computed(() => {
    const size = normalizeSize(props.size)
    return {
        width: size,
        height: size,
    }
})

const initAnimation = async () => {
    if (!containerRef.value) return

    const lottie = (await import('lottie-web')).default

    animation?.destroy()
    animation = lottie.loadAnimation({
        container: containerRef.value,
        renderer: 'svg',
        loop: props.loop,
        autoplay: props.autoplay,
        animationData: loadingAnimation,
        rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
        },
    })
}

onMounted(() => {
    initAnimation()
})

onBeforeUnmount(() => {
    animation?.destroy()
    animation = null
})
</script>

<style scoped lang="less">
.app-loading-lottie {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;

    :deep(svg) {
        display: block;
        width: 100%;
        height: 100%;
    }
}
</style>
